import { z } from 'zod';
import { request, requestVoid } from './api-client';

/**
 * The user directory (spec § 4.1).
 *
 * Two endpoints and no more: who am I, and who could approve this. There is deliberately no way to list
 * or search every colleague — see `UsersController`, which explains why a general directory endpoint on
 * an internal tool is an enumeration oracle nothing here needs.
 */

export const ApproverSchema = z.object({
  subject: z.string(),
  email: z.string(),
  displayName: z.string(),
  /** Shown in the picker: assigning to somebody who last signed in months ago is a long wait. */
  lastSeenAt: z.string(),
});

export type Approver = z.infer<typeof ApproverSchema>;

export function listApprovers() {
  return request('/api/v1/users/approvers', z.array(ApproverSchema));
}

/**
 * Registers the signed-in user in the directory.
 *
 * The response is discarded — the session already carries everything the SPA needs about the caller.
 * This is called for its WRITE: it is what puts a person in the directory so a colleague can later pick
 * them as an approver, which is why the directory can only ever list people who have used the app.
 *
 * Failure is swallowed by the caller rather than surfaced. Somebody who cannot be registered can still
 * do their whole job; a red banner on a screen they did not ask for would be noise about a background
 * detail (and an API-key caller answers 204 here by design).
 */
export async function registerVisit(): Promise<void> {
  // requestVoid, not a bare fetch: it is what attaches the bearer token and resolves the API base URL.
  // A hand-rolled fetch here would arrive unauthenticated and 401 on every boot.
  await requestVoid('/api/v1/users/me');
}
